// src/pages/LandingPage.tsx — Site officiel Vanessa
import { Link } from 'react-router-dom';
import { MessageCircle, Eye, MapPin, Mic, ShieldCheck, Code2, Handshake, Heart } from 'lucide-react';
import { Container, Section, FeatureCard, Grid, CtaBand, CodeBlock } from '@/components/site/blocks';
import { buttonClasses } from '@/components/ui/button';
import { APP_OPEN_URL } from '@/config/site';

export default function LandingPage() {
    return (
        <>
            {/* Hero */}
            <div className="border-b border-gray-200 bg-gradient-to-b from-brand-tint to-cream">
                <Container className="grid items-center gap-10 py-14 sm:py-20 lg:grid-cols-[1.2fr_1fr]">
                    <div>
                        <p className="mb-4 inline-block rounded-full bg-white px-3 py-1 text-sm font-semibold text-ochre ring-1 ring-brand">
                            Le site officiel de Vanessa
                        </p>
                        <h1 className="font-display text-4xl font-bold leading-[1.1] text-ink sm:text-6xl">
                            Celle qui vous ressemble et vous rassemble.
                        </h1>
                        <p className="mt-5 max-w-xl text-lg leading-relaxed text-gray-700">
                            Vanessa est une intelligence artificielle au ton des jeunes africains. Discutez avec elle,
                            retrouvez les histoires de Ça Parle et les bons plans de Ça sert, ou intégrez-la dans vos
                            propres projets.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a href={APP_OPEN_URL} className={buttonClasses('primary', 'lg')}>Ouvrir l’application</a>
                            <Link to="/vanessa" className={buttonClasses('secondary', 'lg')}>Découvrir Vanessa</Link>
                        </div>
                    </div>

                    {/* Illustration : Vanessa et un exemple d'échange */}
                    <div className="mx-auto w-full max-w-sm" aria-label="Exemple de conversation avec Vanessa">
                        <div className="rounded-3xl border border-gray-200 bg-white p-5 shadow-lg">
                            <div className="mb-4 flex items-center gap-3">
                                <img src="/vanessa-avatar.png" alt="" className="h-14 w-14 rounded-full object-cover ring-4 ring-brand" />
                                <div>
                                    <p className="font-display text-lg font-bold text-ink">Vanessa</p>
                                    <p className="text-sm text-gray-600">19 ans · Bénin, Cameroun, Côte d’Ivoire</p>
                                </div>
                            </div>
                            <div className="space-y-3 text-base">
                                <p className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-sm bg-brand px-4 py-2.5 text-ink">
                                    Salut Vanessa, j’ai un truc à te raconter…
                                </p>
                                <p className="w-fit max-w-[85%] rounded-2xl rounded-bl-sm bg-sand px-4 py-2.5 text-ink">
                                    Wèèh, raconte-moi ça bien, qu’est-ce qui s’est passé exactement ? 👀
                                </p>
                            </div>
                            <p className="mt-4 text-sm text-gray-600">Exemple de conversation.</p>
                        </div>
                    </div>
                </Container>
            </div>

            {/* Qui est Vanessa */}
            <Section
                title="Pas un assistant comme les autres"
                intro="Vanessa parle comme on parle entre amis : en français de rue, avec les expressions du Bénin, de Côte d’Ivoire, du Togo, du Cameroun et du Sénégal. Jamais comme un service client."
            >
                <Grid cols={3}>
                    <FeatureCard icon={Heart} title="Un vrai caractère">
                        Curieuse, moqueuse, détective ou confidente : elle change d’humeur selon la conversation.
                    </FeatureCard>
                    <FeatureCard icon={MessageCircle} title="Elle vous connaît">
                        Elle retient certaines choses d’une conversation à l’autre. Vous voyez et effacez ce qu’elle retient, quand vous voulez.
                    </FeatureCard>
                    <FeatureCard icon={ShieldCheck} title="Sérieuse quand il le faut" to="/vanessa" linkLabel="Tout savoir sur Vanessa">
                        Sur les sujets graves, elle reste factuelle. Elle ne commente jamais le physique de personne.
                    </FeatureCard>
                </Grid>
            </Section>

            {/* Produits */}
            <Section
                tone="sand"
                title="Une plateforme, quatre façons de la retrouver"
            >
                <Grid cols={4}>
                    <FeatureCard icon={MessageCircle} title="Discuter" to="/produits/discuter" linkLabel="Voir">
                        En texte, en vocal ou avec une photo.
                    </FeatureCard>
                    <FeatureCard icon={Eye} title="Ça Parle" to="/produits/ca-parle" linkLabel="Voir">
                        Les histoires du moment, à lire en plein écran et à commenter.
                    </FeatureCard>
                    <FeatureCard icon={MapPin} title="Ça sert" to="/produits/ca-sert" linkLabel="Voir">
                        La carte des bons plans et des prix, partagés par la communauté.
                    </FeatureCard>
                    <FeatureCard icon={Mic} title="Émissions" to="/produits/emissions" linkLabel="Voir">
                        Parlez à Vanessa à voix haute.
                    </FeatureCard>
                </Grid>
            </Section>

            {/* Confiance */}
            <Section
                title="Pensée pour rester sûre"
                intro="Une IA qui parle comme les jeunes doit aussi les protéger. Ces règles passent avant le style."
            >
                <Grid cols={3}>
                    <FeatureCard icon={ShieldCheck} title="Pas de fausses rumeurs">
                        Vanessa n’invente pas d’accusations sur des personnes réelles nommées.
                    </FeatureCard>
                    <FeatureCard icon={Heart} title="Face à la détresse">
                        Si quelqu’un va mal, elle sort du ton léger, écoute, et invite à en parler à un adulte de confiance ou à un professionnel.
                    </FeatureCard>
                    <FeatureCard icon={Eye} title="Conversations privées" to="/confiance" linkLabel="Confiance & sécurité">
                        Chaque conversation reste cloisonnée : Vanessa ne répète rien de ce que lui disent les autres.
                    </FeatureCard>
                </Grid>
            </Section>

            {/* Développeurs & entreprises */}
            <Section tone="sand">
                <div className="grid items-center gap-10 lg:grid-cols-2">
                    <div>
                        <p className="mb-2 flex items-center gap-2 font-display font-semibold text-ochre">
                            <Code2 className="h-5 w-5" aria-hidden="true" /> Pour les développeurs
                        </p>
                        <h2 className="font-display text-3xl font-bold text-ink">Vanessa dans votre application</h2>
                        <p className="mt-3 text-lg text-gray-700">
                            Une seule requête, et votre site, bot ou outil parle avec le ton de Vanessa. Clé API, suivi de
                            l’usage et quota en temps réel depuis votre console.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/developpeurs/demarrage" className={buttonClasses('primary', 'md')}>Démarrage rapide</Link>
                            <Link to="/documentation" className={buttonClasses('secondary', 'md')}>Documentation</Link>
                        </div>
                        <p className="mt-8 flex items-center gap-2 font-display font-semibold text-ochre">
                            <Handshake className="h-5 w-5" aria-hidden="true" /> Pour les entreprises et partenaires
                        </p>
                        <p className="mt-2 text-base text-gray-700">
                            Médias, associations, institutions : Vanessa peut porter vos sujets auprès de la communauté.{' '}
                            <Link to="/entreprises" className="font-semibold text-ochre underline">Découvrir les partenariats</Link>
                        </p>
                    </div>
                    <CodeBlock label="Exemple de requête">{`curl https://api.kinemaplus.com/v1 \\
  -H "x-api-key: cp_live_votre_cle" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [
      { "role": "user", "content": "Salut Vanessa !" }
    ]
  }'`}</CodeBlock>
                </div>
            </Section>

            <CtaBand
                title="Prêt à parler avec Vanessa ?"
                text="Elle vous attend. Un compte suffit pour discuter, publier et explorer les bons plans."
                primary={{ label: 'Ouvrir l’application', href: APP_OPEN_URL }}
                secondary={{ label: 'Lire le guide d’utilisation', to: '/guide' }}
            />
        </>
    );
}
